import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { token } from '@atlaskit/tokens';

import { type MentionAgentsSpotlightPostOfficeMessage } from '../../MentionAgentsSpotlightPostOfficeMessage';

import { messages } from './messages';

const styles = cssMap({
	spotlightTarget: {
		position: 'absolute',
		width: '1px',
		height: '1px',
		insetInlineStart: token('space.400'),
		insetBlockEnd: 0,
		pointerEvents: 'none',
	},
});

type AgentSourceControlSpotlightProps = {
	isVisible: boolean;
	onDismiss: (source: 'cross-icon' | 'understood-button') => void;
	postOfficeMessage?: MentionAgentsSpotlightPostOfficeMessage;
};

const OFFSET: [number, number] = [-75, 0];

export const AgentSourceControlSpotlight = ({
	isVisible,
	onDismiss,
	postOfficeMessage,
}: AgentSourceControlSpotlightProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!isVisible || !postOfficeMessage) {
		return null;
	}
	const { PostOfficeMessageInstance, messageInstanceId } = postOfficeMessage;
	const popoverContent = (
		<PopoverContent
			dismiss={() => onDismiss('cross-icon')}
			isVisible={isVisible}
			placement="bottom-end"
			offset={OFFSET}
		>
			<SpotlightCard testId="agent-source-control-spotlight">
				<SpotlightHeader>
					<SpotlightHeadline>
						{formatMessage(messages.agentSourceControlSpotlightTitle)}
					</SpotlightHeadline>
					<SpotlightControls>
						<SpotlightDismissControl onClick={() => onDismiss('cross-icon')} />
					</SpotlightControls>
				</SpotlightHeader>
				<SpotlightBody>{formatMessage(messages.agentSourceControlSpotlightBody)}</SpotlightBody>
				<SpotlightFooter>
					<SpotlightActions>
						<SpotlightPrimaryAction onClick={() => onDismiss('understood-button')}>
							{formatMessage(messages.agentSourceControlSpotlightUnderstood)}
						</SpotlightPrimaryAction>
					</SpotlightActions>
				</SpotlightFooter>
			</SpotlightCard>
		</PopoverContent>
	);

	return (
		<PopoverProvider>
			<PopoverTarget>
				<Box xcss={styles.spotlightTarget} />
			</PopoverTarget>
			<PostOfficeMessageInstance messageInstanceId={messageInstanceId}>
				{() => popoverContent}
			</PostOfficeMessageInstance>
		</PopoverProvider>
	);
};
